<template>
  <v-flex>
      <time-agg-panel v-if="showAgg" :agg="centralizedAgg" @apply="applyCentralizedAgg" @clear="clearCentralizedAgg"></time-agg-panel>
      <div ref="content" v-if="loading||(!loading&&hasSomeData)">
      <v-card v-if="!justPlot" flat :light="whiteMode" style="background:transparent !important;">
          <v-card-text v-if="loading">
              <v-layout style="min-height:40px;">
                  <loading-progress-bar :loading="loading" color="secondary"></loading-progress-bar>
              </v-layout>
          </v-card-text>
          <v-card-text :style="mobileMode ? 'padding:0 !important;' : ''">
              <vue-plotly v-if="!loading" :white-mode="whiteMode" :data="data" :show-export="showExport" :disable-responsive="disableResponsive" :layout="{...layout}" :options="{...options}" ref="plotly" @click="handleClick"/>
          </v-card-text>
      </v-card>
          <loading-progress-bar :loading="justPlot&&loading" color="secondary"></loading-progress-bar>
          <vue-plotly v-if="justPlot&&!loading" :white-mode="whiteMode" :show-export="showExport" :data="data" :disable-responsive="disableResponsive" :layout="{...layout}" :options="{...options}" ref="plotly" @click="handleClick"/>
      </div>
      <div v-if="showErrors&&!loading&&errors&&errors.length>0" class="pa-2">
          <v-alert v-for="error in errors"
                  :value="true"
                  style="padding: 6px;"
                  type="error"
          >
              {{error}}
          </v-alert>
      </div>
      <div v-if="showErrors&&!loading&&dataWarnings&&dataWarnings.length>0" class="pa-2">
          <v-alert v-for="dataWarning in dataWarnings"
                   :value="true"
                   style="padding: 6px;"
                   type="warning"
          >
              {{dataWarning.title}} <v-icon small class="ml-2 cursor-as-pointer" @click="dataWarning.showDetail=true">fa-info</v-icon>
              <v-dialog v-model="dataWarning.showDetail" max-width="600">
                  <v-card>
                      <v-card-title class="primary white--text title">
                          {{dataWarning.title}}
                      </v-card-title>
                      <v-card-text>
                              <pre>
                                  {{dataWarning.details}}
                              </pre>
                      </v-card-text>
                      <v-card-actions>
                          <v-spacer></v-spacer>
                          <v-btn color="primary" text @click="dataWarning.showDetail=false">{{'Close'|translate}}</v-btn>
                      </v-card-actions>
                  </v-card>
              </v-dialog>
          </v-alert>
      </div>
      <div v-if="showErrors&&!loading&&missingSensors&&missingSensors.length>0" class="pa-2">
          <v-alert
              :value="true"
              style="padding: 6px;"
              type="warning"
          >
              {{'Missing sensors : '|translate}}
              <v-chip  v-for="missingSensor in missingSensors" >{{missingSensor}}</v-chip>
          </v-alert>
      </div>
  </v-flex>
</template>

<script>
import VuePlotly from './VuePlotly'
import TimeAggPanel from './TimeAggPanel'
import {callAPI} from '@/helper'
import LoadingProgressBar from './LoadingProgressBar'
import unitResolver from "./mixins/unitResolver";
import sensorDescResolver from "./mixins/sensorDescResolver";
import smoothLine from "./mixins/smoothLine";
import data3Processor from "./mixins/data3Processor";

export default {
  props: {
      layoutOverrides: {type: Object, default: null},
      parameters: {type: Object, default: null},
      dateRange: {type: Object, default: null},
      seriesOverrides: {type: Object, default: null},
      xAxis: {type: String, default: 'timestamp'},
      yAxis: {type: String, default: null},
      colorAxis: {type: String, default: null},
      markerSizeAxis: {type: String, default: null},
      colorAxisScale: {type: String, default: null},
      colorAxisTitle: {type: String, default: null},
      dataPayload: {type: Array, default: null},
      showUnits: {type: Boolean, default: true},
      showSensorDesc: {type: Boolean, default: true},
      showExport: {type: Boolean, default: true},
      showAgg: {type: Boolean, default: false},
      justPlot: {type: Boolean, default: false},
      resolveIdsToName: {type: Boolean, default: false},
      onOnly: {type:Boolean, default: false},
      equipment: {type:String, default: null},
      disableResponsive: {type: Boolean, default: false},
      showErrors: {type: Boolean, default: false},
      independentX: {type: Boolean, default: false},
      whiteMode: {type: Boolean, default: false},
  },
    mixins:[unitResolver,sensorDescResolver, smoothLine,data3Processor],
  components: {
    LoadingProgressBar,
    VuePlotly,
    TimeAggPanel
  },
  computed: {
      mobileMode(){
          return process.env.VUE_APP_MOBILE_MODE ? process.env.VUE_APP_MOBILE_MODE : false
      },
    layout(){
        let settings={
            xaxis: {
                title: this.$options.filters.translate("Time")
            },
            legend: {
                orientation: 'h',
                traceorder:'normal',
                y:this.mobileMode ? -0.4 : -0.3
            },
            showlegend:true,
            hovermode:"x",
            hoverlabel: {
                namelength: -1,
            },
            height:300,
            margin:{
                l: 50,
                r: 50,
                t: 30,
                pad: 1
            },
        };
        if(this.layoutOverrides){
            settings=Object.assign({}, settings, JSON.parse(JSON.stringify(this.layoutOverrides)));
        }
        for (let axis of ["xaxis","yaxis", "zaxis", "yaxis2"]){
            if (settings[axis]&&settings[axis].useCustomAxis){
                settings[axis].range=[settings[axis].customAxisMin,settings[axis].customAxisMax]
            }
        }
        if(settings.xaxis&&settings.xaxis.limitTicksToData&&this.xAxisValues){
            settings.xaxis.nticks=this.xAxisValues.length+1
        }
        if (this.yAxis){
            settings.scene={
                xaxis: settings.xaxis,
                yaxis: settings.yaxis,
                zaxis: settings.zaxis,
            }
        }
        if(settings.showlegend&&settings.automatedLegendPosition){
            if (settings.automatedLegendPosition==="top"){
                settings.legend={
                    orientation: 'h',
                    traceorder:'normal',
                    y:1.1
                }
            } else if (settings.automatedLegendPosition==="right"){
                settings.legend={
                    orientation: 'v',
                    traceorder:'normal',
                    y:1,
                    x:1
                }
            } else if (settings.automatedLegendPosition==="left"){
                settings.legend={
                    orientation: 'v',
                    traceorder:'normal',
                    y:1,
                    x:0
                }
            }
        }
        if (this.mobileMode){
            if ((!settings.margin.r||settings.margin.r===50)&&(!settings.yaxis2||!settings.yaxis2.title||settings.yaxis2.title==="")){
                settings.margin.r=10
            }
            if ((!settings.margin.l||settings.margin.l===50)&&(!settings.yaxis||!settings.yaxis.title||settings.yaxis.title==="")){
                settings.margin.l=10
            }
        }
      return JSON.parse(JSON.stringify(settings));
    }
  },
  name: 'Data3Chart',
  watch: {
      $props: {
          handler() {
              this.refresh()
          },
          deep: true,
          immediate: true,
      },
  },
  data: function () {
    return {
        data: [],
        errors: [],
        dataWarnings: [],
        missingSensors: [],
        timeout: null,
        facility_name: null,
        hasSomeData: false,
        options: {},
        df: null,
        xAxisValues: null,
        yAxisValues: null,
        colorAxisValues: null,
        markerSizeAxisValues: null,
        payloadCache:null,
        cachedResult:null,
        reloadInc:0,
        loading: true,
        aggApplied: false,
        centralizedAgg: {type:'agg', agg:'H', agg_op:'sum', divide_by_sf: true},
    }
    },
    methods: {
        handleClick(data) {
            if (this.layoutOverrides.jumpToCVOnX && this.layoutOverrides.jumpToCVOnX!=="" && data && data.points && data.points[0] && data.points[0].x) {
                let query={equipment:data.points[0].x}
                if (this.dateRange&&this.dateRange.start){
                    query.rangeStart=this.dateRange.start
                }
                if (this.dateRange&&this.dateRange.end){
                    query.rangeEnd=this.dateRange.end
                }
                this.$router.push({
                    path: '/custom-view/' + this.layoutOverrides.jumpToCVOnX,
                    query: query
                })
            } else if (this.layoutOverrides.jumpToCVOnY && this.layoutOverrides.jumpToCVOnY!=="" && data && data.points && data.points[0] && data.points[0].y) {
                let query={equipment:data.points[0].y}
                if (this.dateRange&&this.dateRange.start){
                    query.rangeStart=this.dateRange.start
                }
                if (this.dateRange&&this.dateRange.end){
                    query.rangeEnd=this.dateRange.end
                }
                this.$router.push({
                    path: '/custom-view/' + this.layoutOverrides.jumpToCVOnY,
                    query: query
                })
            } else {
                this.$emit('plotclick', data)
            }
      },
      applyCentralizedAgg() {
          this.aggApplied = true
          this.refresh()
      },
      clearCentralizedAgg() {
          this.aggApplied = false
          this.refresh()
      },
      debounce(callback, wait) {
          clearTimeout(this.timeout);
          this.timeout= setTimeout(() => callback(), wait);
      },
      refresh(){
          this.debounce(this.doRefresh,500);
      },
      async doRefresh() {
          let me = this;
          this.loading = true
          this.errors = []
          this.dataWarnings=[]
          this.missingSensors=[]
          this.reloadInc=this.reloadInc+1
          let currentInc=this.reloadInc
          if (this.dataPayload) {
              this.data = []
              let colorscaleValue = [
                  [0, this.unifiedIsThemeDark ? "rgb(31, 119, 180)" : '#0000FF'],
                  [1, this.unifiedIsThemeDark ? "rgb(214, 39, 40)" : '#FF0000']
              ]
              let payloadArray=JSON.parse(JSON.stringify(this.dataPayload))
              payloadArray=payloadArray.filter(x=>x.filters&&(x.filters.sensor_name||x.filters.equipment_name))
              let pcArray=[]
              for (let payload of payloadArray){
                      payload.parameters=this.parameters
                      this.processDateRange(payload, this.dateRange)
                      if(!payload.filters.facility_name&&this.facility_name){
                          payload.filters.facility_name=this.facility_name
                      }
                      if(this.equipment){
                          this.processEquipment(payload, this.equipment)
                      }
                      if(this.onOnly||payload.onOnly){
                          if(!payload.pipeline){
                              payload.pipeline=[]
                          }
                          payload.pipeline.unshift(
                              {
                                  "type": "filter",
                                  "filter_op": "eq",
                                  "filter_by": "* - on",
                                  "filter_value": 1,
                              }
                          )
                      }
                      if(this.aggApplied){
                          if(!payload.pipeline){
                              payload.pipeline=[]
                          }
                          payload.pipeline=payload.pipeline.filter(x=>x.type!=='agg')
                          payload.pipeline.push(JSON.parse(JSON.stringify(this.centralizedAgg)))
                      }
                  let {seriesOverrides, ...payloadBase} = payload
                  pcArray.push(payloadBase)
              }
              let newPayloadCache=JSON.stringify(pcArray)
              let results=[]
              if(newPayloadCache===me.payloadCache&&me.cachedResult){
                  results = JSON.parse(JSON.stringify(me.cachedResult))
              } else {
                  results = await Promise.all(payloadArray.map(p=>callAPI('data3/',p)))
                  if (currentInc!==this.reloadInc){
                      return
                  }
                  me.cachedResult=JSON.parse(JSON.stringify(results))
                  me.payloadCache=newPayloadCache
              }
                  let newData=[]
                  me.xAxisValues=null
                  me.yAxisValues=null
                  me.colorAxisValues=null
                  me.markerSizeAxisValues=null
                  let configIndex=0
                  let hasSomeData=false
                  for (let result of results){
                      if(result.data.error){
                          me.errors.push(result.data.error)
                          continue
                      }
                      if (result.data.timestamp&&result.data.timestamp.length>0){
                          hasSomeData=true
                      }
                      for (let seriesIndex in result.data) {
                          if (!me.xAxisValues&&(seriesIndex === me.xAxis||seriesIndex.split(" - ")[1]===me.xAxis)&&result.data[seriesIndex]&&result.data[seriesIndex].length>0) {
                              me.xAxisValues = result.data[seriesIndex]
                          }
                          if (!me.yAxisValues&&me.yAxis&&(seriesIndex === me.yAxis||seriesIndex.split(" - ")[1]===me.yAxis)){
                              me.yAxisValues = result.data[seriesIndex]
                          }
                          if (!me.colorAxisValues&&me.colorAxis&&(seriesIndex === me.colorAxis||seriesIndex.split(" - ")[1]===me.colorAxis)){
                              me.colorAxisValues = result.data[seriesIndex]
                          }
                          if (!me.markerSizeAxisValues&&me.markerSizeAxis&&(seriesIndex === me.markerSizeAxis||seriesIndex.split(" - ")[1]===me.markerSizeAxis)){
                              me.markerSizeAxisValues = result.data[seriesIndex]
                          }
                      }
                  }
                  if (!hasSomeData){
                      me.dataWarnings.push({title:"No data for this chart",details:JSON.stringify(payloadArray, null, 4),showDetail:false})
                  }
                  me.hasSomeData=hasSomeData
                  if (me.showErrors){
                      me.missingSensors=this.findMissingSensors(pcArray)
                  }
                  if (!me.xAxisValues){
                      me.xAxisValues=[]
                  }
                  for (let result of results){
                      if(result.data.error){
                          continue
                      }
                      if (hasSomeData&&(!result.data.timestamp||result.data.timestamp.length===0)){
                          me.dataWarnings.push({title:"No data for query "+(results.indexOf(result)+1),details:JSON.stringify(payloadArray[results.indexOf(result)], null, 4),showDetail:false})
                      }
                      if(payloadArray[configIndex].seriesOverrides.type&&payloadArray[configIndex].seriesOverrides.type==="histogram2d"&&me.xAxis==="timestamp"){
                          me.errors.push("Cannot plot 2d histogram using timestamp as X axis")
                          continue
                      }
                      if(payloadArray[configIndex].seriesOverrides.type&&payloadArray[configIndex].seriesOverrides.type==="heatmap"){
                          let newSeries={
                              x:result.data[me.xAxis],
                              y:[],
                              z:[],
                              type: 'heatmap',
                              colorscale: payloadArray[configIndex].seriesOverrides.colorscale ? payloadArray[configIndex].seriesOverrides.colorscale : colorscaleValue,
                              showscale:payloadArray[configIndex].seriesOverrides.showscale ? payloadArray[configIndex].seriesOverrides.showscale : false,
                              zmin:payloadArray[configIndex].seriesOverrides.heatmapZmin,
                              zmax:payloadArray[configIndex].seriesOverrides.heatmapZmax
                          }
                          for (let seriesIndex in result.data){
                              if(seriesIndex===me.xAxis||seriesIndex==="timestamp"){
                                  continue
                              }
                              let newRow={
                                  data:result.data[seriesIndex],
                                  name:seriesIndex
                              }
                              if(payloadArray[configIndex].seriesOverrides.equipOnlyName&&seriesIndex.split(" - ")[1]){
                                  newRow.name=seriesIndex.split(" - ")[0]
                              } else {
                                  if(me.showSensorDesc&&seriesIndex.split(" - ")[1]){
                                      let sensorDesc=me.getSensorDesc(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                      if(sensorDesc){
                                          if (me.layout.descOnlyName){
                                              newRow.name=sensorDesc
                                          } else {
                                              newRow.name=newRow.name+" - "+sensorDesc
                                          }
                                      }
                                  }
                                  if(me.showUnits&&seriesIndex.split(" - ")[1]){
                                      let unit=me.getUnit(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                      if(payloadArray[configIndex].seriesOverrides.customUnit){
                                          unit = payloadArray[configIndex].seriesOverrides.customUnit
                                      }
                                      let unitbox=unit&&unit!=="_" ? " ("+unit+")" : ''
                                      newRow.name=newRow.name+unitbox
                                  }
                              }
                              newSeries.y.push(newRow.name)
                              newSeries.z.push(newRow.data)

                          }
                          newData.push(newSeries)
                      } else if(payloadArray[configIndex].seriesOverrides.type&&payloadArray[configIndex].seriesOverrides.type==="pie"){
                          let newSeries={
                              labels:[],
                              values:[],
                              sort:false,
                              type: 'pie',
                              hole:payloadArray[configIndex].seriesOverrides.hole ? payloadArray[configIndex].seriesOverrides.hole : null
                          }
                          for (let seriesIndex in result.data){
                              if(seriesIndex===me.xAxis||seriesIndex==="timestamp"){
                                  continue
                              }
                              let newRow={
                                  data:result.data[seriesIndex],
                                  name:seriesIndex
                              }
                              if(payloadArray[configIndex].seriesOverrides.equipOnlyName&&seriesIndex.split(" - ")[1]){
                                  newRow.name=seriesIndex.split(" - ")[0]
                              } else {
                                  if(me.showSensorDesc&&seriesIndex.split(" - ")[1]){
                                      let sensorDesc=me.getSensorDesc(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                      if(sensorDesc) {
                                          if (me.layout.descOnlyName) {
                                              newRow.name = sensorDesc
                                          } else {
                                              newRow.name = newRow.name + " - " + sensorDesc
                                          }
                                      }
                                  }
                                  if(me.showUnits&&seriesIndex.split(" - ")[1]){
                                      let unit=me.getUnit(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                      if(payloadArray[configIndex].seriesOverrides.customUnit){
                                          unit = payloadArray[configIndex].seriesOverrides.customUnit
                                      }
                                      let unitbox=unit&&unit!=="_" ? " ("+unit+")" : ''
                                      newRow.name=newRow.name+unitbox
                                  }
                              }
                              newSeries.labels.push(newRow.name)
                              newSeries.values.push(newRow.data.reduce((acc, curr) => curr!==null ? curr : acc))
                          }
                          newData.push(newSeries)
                      } else if(payloadArray[configIndex].seriesOverrides.type&&(payloadArray[configIndex].seriesOverrides.type==="scatterpolar"||payloadArray[configIndex].seriesOverrides.type==="barpolar")){
                          let newSeries={
                              r:[],
                              theta:[],
                              type: payloadArray[configIndex].seriesOverrides.type,
                              name:payloadArray[configIndex].seriesOverrides.nameOverride,
                          }
                          if(payloadArray[configIndex].seriesOverrides){
                              newSeries=Object.assign({}, newSeries, payloadArray[configIndex].seriesOverrides)
                          } else if (me.seriesOverrides){
                              newSeries=Object.assign({}, newSeries, me.seriesOverrides)
                          }
                          for (let seriesIndex in result.data){
                              if(seriesIndex===me.xAxis||seriesIndex==="timestamp"){
                                  continue
                              }
                              let newRow={
                                  data:result.data[seriesIndex],
                                  name:seriesIndex
                              }
                              if(payloadArray[configIndex].seriesOverrides.equipOnlyName&&seriesIndex.split(" - ")[1]){
                                  newRow.name=seriesIndex.split(" - ")[0]
                              } else {
                                  if(me.showSensorDesc&&seriesIndex.split(" - ")[1]){
                                      let sensorDesc=me.getSensorDesc(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                      if(sensorDesc) {
                                          if (me.layout.descOnlyName) {
                                              newRow.name = sensorDesc
                                          } else {
                                              newRow.name = newRow.name + " - " + sensorDesc
                                          }
                                      }
                                  }
                                  if(me.showUnits&&seriesIndex.split(" - ")[1]){
                                      let unit=me.getUnit(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                      if(payloadArray[configIndex].seriesOverrides.customUnit){
                                          unit = payloadArray[configIndex].seriesOverrides.customUnit
                                      }
                                      let unitbox=unit&&unit!=="_" ? " ("+unit+")" : ''
                                      newRow.name=newRow.name+unitbox
                                  }
                              }
                              newSeries.theta.push(newRow.name)
                              newSeries.r.push(newRow.data.reduce((acc, curr) => curr!==null ? curr : acc))
                          }
                          newData.push(newSeries)
                      } else if(payloadArray[configIndex].seriesOverrides.type&&payloadArray[configIndex].seriesOverrides.type==="parallel coordinates"){
                          let newSeries={
                              type: 'parcoords',
                              dimensions:[]
                          }
                          if (me.colorAxis&&me.colorAxisValues){
                            newSeries.line={
                                showscale: payloadArray[configIndex].seriesOverrides.showscale ? payloadArray[configIndex].seriesOverrides.showscale : false,
                                colorscale: payloadArray[configIndex].seriesOverrides.colorscale ? payloadArray[configIndex].seriesOverrides.colorscale : colorscaleValue,
                                color: me.colorAxisValues
                            }
                          }
                          for (let seriesIndex in result.data){
                              if(seriesIndex===me.xAxis||seriesIndex==="timestamp"){
                                  continue
                              }
                              if(me.colorAxis&&(seriesIndex === me.colorAxis||seriesIndex.split(" - ")[1]===me.colorAxis)){
                                  continue
                              }
                              let newRow={
                                  values:result.data[seriesIndex],
                                  label:seriesIndex
                              }
                              if(payloadArray[configIndex].seriesOverrides.equipOnlyName&&seriesIndex.split(" - ")[1]){
                                  newRow.label=seriesIndex.split(" - ")[0]
                              } else {
                                  if(me.showSensorDesc&&seriesIndex.split(" - ")[1]){
                                      let sensorDesc=me.getSensorDesc(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                      if(sensorDesc) {
                                          if (me.layout.descOnlyName) {
                                              newRow.label = sensorDesc
                                          } else {
                                              newRow.label = newRow.label + " - " + sensorDesc
                                          }
                                      }
                                  }
                                  if(me.showUnits&&seriesIndex.split(" - ")[1]){
                                      let unit=me.getUnit(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                      if(payloadArray[configIndex].seriesOverrides.customUnit){
                                          unit = payloadArray[configIndex].seriesOverrides.customUnit
                                      }
                                      let unitbox=unit&&unit!=="_" ? " ("+unit+")" : ''
                                      newRow.label=newRow.label+unitbox
                                  }
                              }
                              newSeries.dimensions.push(newRow)
                          }
                          if(newSeries.dimensions.length>0){
                              newData.push(newSeries)
                          }
                      } else if(payloadArray[configIndex].seriesOverrides.type&&["bar summary","scatter summary"].indexOf(payloadArray[configIndex].seriesOverrides.type)>-1){
                          let newSeries={
                              x:[],
                              y:[],
                              type: payloadArray[configIndex].seriesOverrides.type==="bar summary" ? 'bar' : 'scatter',
                              summaryType:payloadArray[configIndex].seriesOverrides.type,
                              name:payloadArray[configIndex].seriesOverrides.nameOverride
                          }
                          if(newSeries.type==='scatter'){
                              newSeries.mode='markers'
                          }
                          if (newSeries.type==="bar"&&payloadArray[configIndex].seriesOverrides.horizontalSummaryMode) {
                              newSeries.orientation = "h"
                          }
                          let handleColorSteps=false
                          if (newSeries.type==="bar"&&payloadArray[configIndex].seriesOverrides.steps&&payloadArray[configIndex].seriesOverrides.steps.length>0){
                              handleColorSteps=true
                              newSeries.marker={
                                  color:[]
                              }
                          }
                          for (let seriesIndex in result.data){
                              if(seriesIndex===me.xAxis||seriesIndex==="timestamp"){
                                  continue
                              }
                              let newRow={
                                  data:result.data[seriesIndex],
                                  name:seriesIndex
                              }

                              if(payloadArray[configIndex].seriesOverrides.equipOnlyName&&seriesIndex.split(" - ")[1]){
                                  newRow.name=seriesIndex.split(" - ")[0]
                              } else {
                                  if(me.showSensorDesc&&seriesIndex.split(" - ")[1]){
                                      let sensorDesc=me.getSensorDesc(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                      if(sensorDesc){
                                          if (me.layout.descOnlyName){
                                              newRow.name=sensorDesc
                                          } else {
                                              newRow.name=newRow.name+" - "+sensorDesc
                                          }
                                      }
                                  }
                                  if(me.showUnits&&seriesIndex.split(" - ")[1]){
                                      let unit=me.getUnit(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                      if(payloadArray[configIndex].seriesOverrides.customUnit){
                                          unit = payloadArray[configIndex].seriesOverrides.customUnit
                                      }
                                      let unitbox=unit&&unit!=="_" ? " ("+unit+")" : ''
                                      newRow.name=newRow.name+unitbox
                                  }
                              }
                              let value=newRow.data.reduce((acc, curr) => curr!==null ? curr : acc)
                              if (payloadArray[configIndex].seriesOverrides.horizontalSummaryMode){
                                  newSeries.y.push(newRow.name)
                                  newSeries.x.push(value)
                              } else {
                                  newSeries.x.push(newRow.name)
                                  newSeries.y.push(value)
                              }
                              if (handleColorSteps){
                                  let newColor=this.whiteMode ? "#424242" : "#FFFFFF"
                                  for (let step of payloadArray[configIndex].seriesOverrides.steps){
                                      if (value>=step.range[0]&&value<=step.range[1]){
                                          newColor=step.color
                                          break
                                      }
                                  }
                                  newSeries.marker.color.push(newColor)
                              }
                          }
                          if (newSeries.type==="bar"&&payloadArray[configIndex].seriesOverrides.showValuesOnBars){
                              newSeries.text=payloadArray[configIndex].seriesOverrides.horizontalSummaryMode ? newSeries.x.map(x=>x!==null ? parseFloat((x).toFixed(2)) : "") : newSeries.y.map(y=>y!==null ? parseFloat((y).toFixed(2)) : "")
                              newSeries.textposition= 'auto'
                          }
                          if(newSeries.type==="bar"){
                              for (let transfered of ["barmode","stackgroup","disableStacking"]){
                                  if (payloadArray[configIndex].seriesOverrides[transfered]){
                                      newSeries[transfered]=payloadArray[configIndex].seriesOverrides[transfered]
                                  }
                              }
                          }
                          newData.push(newSeries)
                      } else if(payloadArray[configIndex].seriesOverrides.type&&payloadArray[configIndex].seriesOverrides.type==="histogram"){
                          for (let seriesIndex in result.data){
                              if(seriesIndex==="timestamp"){
                                  continue
                              }
                              let newSeries={
                                  x:result.data[seriesIndex],
                                  y:result.data[seriesIndex],
                                  name:seriesIndex
                              }
                              if(payloadArray[configIndex].seriesOverrides){
                                  newSeries=Object.assign({}, newSeries, payloadArray[configIndex].seriesOverrides)
                              } else if (me.seriesOverrides){
                                  newSeries=Object.assign({}, newSeries, me.seriesOverrides)
                              }
                              if(me.showSensorDesc&&seriesIndex.split(" - ")[1]){
                                  let sensorDesc=me.getSensorDesc(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                  if(sensorDesc) {
                                      if (me.layout.descOnlyName) {
                                          newSeries.name = sensorDesc
                                      } else {
                                          newSeries.name = newSeries.name + " - " + sensorDesc
                                      }
                                  }
                              }
                              if(me.showUnits&&seriesIndex.split(" - ")[1]){
                                  let unit=me.getUnit(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                  if(payloadArray[configIndex].seriesOverrides.customUnit){
                                      unit = payloadArray[configIndex].seriesOverrides.customUnit
                                  }
                                  let unitbox=unit&&unit!=="_" ? " ("+unit+")" : ''
                                  newSeries.name=newSeries.name+unitbox
                              }
                              newData.push(newSeries)
                          }
                      }else if(payloadArray[configIndex].seriesOverrides.type&&payloadArray[configIndex].seriesOverrides.type==="table"){
                          let copiedResult=JSON.parse(JSON.stringify(result.data))
                          delete (copiedResult[me.xAxis])
                          delete (copiedResult["timestamp"])
                          let newSeries = {
                              type: 'table',
                              header: {
                                  values: ["<b>"+me.layout.xaxis.title+"</b>",...Object.keys(copiedResult).map(seriesIndex=>{
                                      let name = seriesIndex
                                      if(me.showSensorDesc&&seriesIndex.split(" - ")[1]){
                                          let sensorDesc=me.getSensorDesc(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                          if(sensorDesc) {
                                              if (me.layout.descOnlyName) {
                                                  name = sensorDesc
                                              } else {
                                                  name=name+" - "+sensorDesc
                                              }
                                          }
                                      }
                                      if (me.showUnits&&seriesIndex.split(" - ")[1]){
                                          let unit=me.getUnit(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                          if(payloadArray[configIndex].seriesOverrides.customUnit){
                                              unit = payloadArray[configIndex].seriesOverrides.customUnit
                                          }
                                          let unitbox=unit&&unit!=="_" ? " ("+unit+")" : ''
                                          name=name+unitbox
                                      }
                                      return(["<b>"+name+"</b>"])
                                  })],
                                  align: "center",
                                  line: {width: 1, color: 'black'},
                                  fill: {color: "gray"},
                                  font: {family: "Arial", size: 12, color: "white"}
                              },
                              cells: {
                                  values: [me.xAxisValues,...Object.values(copiedResult)],
                                  align: "center",
                                  line: {color: "black", width: 1},
                                  fill: {color: "white"},
                                  font: {family: "Arial", size: 11, color: ["black"]}
                              }
                          }
                          if(payloadArray[configIndex].seriesOverrides){
                              newSeries=Object.assign({}, newSeries, payloadArray[configIndex].seriesOverrides)
                          } else if (me.seriesOverrides){
                              newSeries=Object.assign({}, newSeries, me.seriesOverrides)
                          }
                          newData.push(newSeries)

                      }else if(payloadArray[configIndex].seriesOverrides.type&&(payloadArray[configIndex].seriesOverrides.type==="box"||payloadArray[configIndex].seriesOverrides.type==="violin")){
                          for (let seriesIndex in result.data){
                              if(seriesIndex==="timestamp"){
                                  continue
                              }
                              let newSeries={
                                  y:result.data[seriesIndex],
                                  name:seriesIndex
                              }
                              if(payloadArray[configIndex].seriesOverrides&&payloadArray[configIndex].seriesOverrides.horizontalBox){
                                  newSeries={
                                      x:result.data[seriesIndex],
                                      name:seriesIndex
                                  }
                              }
                              if(payloadArray[configIndex].seriesOverrides){
                                  newSeries=Object.assign({}, newSeries, payloadArray[configIndex].seriesOverrides)
                              } else if (me.seriesOverrides){
                                  newSeries=Object.assign({}, newSeries, me.seriesOverrides)
                              }
                              if (newSeries.type==="violin"){
                                  newSeries.box={
                                      visible:true
                                  }
                              }
                              if(me.showSensorDesc&&seriesIndex.split(" - ")[1]){
                                  let sensorDesc=me.getSensorDesc(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                  if(sensorDesc) {
                                      if (me.layout.descOnlyName) {
                                          newSeries.name = sensorDesc
                                      } else {
                                          newSeries.name = newSeries.name + " - " + sensorDesc
                                      }
                                  }
                              }
                              if(me.showUnits&&seriesIndex.split(" - ")[1]){
                                  let unit=me.getUnit(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                  if(payloadArray[configIndex].seriesOverrides.customUnit){
                                      unit = payloadArray[configIndex].seriesOverrides.customUnit
                                  }
                                  let unitbox=unit&&unit!=="_" ? " ("+unit+")" : ''
                                  newSeries.name=newSeries.name+unitbox
                              }
                              newData.push(newSeries)
                          }
                      }else if(payloadArray[configIndex].seriesOverrides.type&&payloadArray[configIndex].seriesOverrides.type==="indicator"){
                          for (let seriesIndex in result.data){
                              if(seriesIndex==="timestamp"){
                                  continue
                              }
                              let newSeries={
                                  value:result.data[seriesIndex].reduce((acc, curr) => curr!==null ? curr : acc),
                                  name:seriesIndex
                              }
                              if(payloadArray[configIndex].seriesOverrides){
                                  newSeries=Object.assign({}, newSeries, payloadArray[configIndex].seriesOverrides)
                              } else if (me.seriesOverrides){
                                  newSeries=Object.assign({}, newSeries, me.seriesOverrides)
                              }
                              if(me.showSensorDesc&&seriesIndex.split(" - ")[1]){
                                  let sensorDesc=me.getSensorDesc(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                  if(sensorDesc) {
                                      if (me.layout.descOnlyName) {
                                          newSeries.name = sensorDesc
                                      } else {
                                          newSeries.name = newSeries.name + " - " + sensorDesc
                                      }
                                  }
                              }
                              if(me.showUnits&&seriesIndex.split(" - ")[1]){
                                  let unit=me.getUnit(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                  if(payloadArray[configIndex].seriesOverrides.customUnit){
                                      unit = payloadArray[configIndex].seriesOverrides.customUnit
                                  }
                                  let unitbox=unit&&unit!=="_" ? " ("+unit+")" : ''
                                  newSeries.name=newSeries.name+unitbox
                              }
                              newSeries.title= { text: newSeries.name }
                              newSeries.number={
                                  font:{
                                      size:newSeries.numberSize
                                  }
                              }
                              if(newSeries.mode==="gauge+number"){
                                  newSeries.gauge= {
                                      steps: newSeries.steps,
                                      axis: { range: [newSeries.gaugeMin, newSeries.gaugeMax] },
                                      bar:{color:newSeries.barColor ? newSeries.barColor: "rgb(31, 119, 180)"},
                                      bgcolor:newSeries.bgColor,
                                      bordercolor: newSeries.borderColor ? newSeries.borderColor : "white",
                                      shape: newSeries.gaugeShape ? newSeries.gaugeShape : "angular",
                                  }
                              } else {
                                  newSeries.mode= "number"
                                  if (newSeries.steps&&newSeries.steps.length>0){
                                      let matching=newSeries.steps.filter(x=>newSeries.value>=x.range[0]).reverse()
                                      if(matching&&matching[0]){
                                          newSeries.number.font.color=matching[0].color
                                      }
                                  }
                              }
                              if(newSeries.carnotNumberFormat&&newSeries.carnotNumberFormat!==""){
                                newSeries.number.valueformat=newSeries.carnotNumberFormat
                              }
                              newData.push(newSeries)
                          }
                      } else {
                          let correspondingX=null;
                          if(me.independentX){
                              if(result.data[me.xAxis]){
                                  correspondingX=me.xAxis
                              } else {
                                  for (let si2 in result.data){
                                      if(si2.split(" - ")[1]===me.xAxis){
                                          correspondingX=si2
                                          break
                                      }
                                  }
                              }
                          }
                          for (let seriesIndex in result.data){
                              if(seriesIndex === me.xAxis||seriesIndex.split(" - ")[1]===me.xAxis||seriesIndex==="timestamp"){
                                  continue
                              }
                              if(me.yAxis&&(seriesIndex === me.yAxis||seriesIndex.split(" - ")[1]===me.yAxis)){
                                  continue
                              }
                              if(me.colorAxis&&(seriesIndex === me.colorAxis||seriesIndex.split(" - ")[1]===me.colorAxis)){
                                  continue
                              }
                              if(me.markerSizeAxis&&(seriesIndex === me.markerSizeAxis||seriesIndex.split(" - ")[1]===me.markerSizeAxis)){
                                  continue
                              }
                              let newSeries={
                                  x:me.independentX ? result.data[correspondingX] : me.xAxisValues,
                                  y:result.data[seriesIndex],
                                  name:seriesIndex
                              }
                              if(me.yAxis){
                                  newSeries={
                                      x:me.independentX ? result.data[correspondingX] : me.xAxisValues,
                                      y:me.yAxisValues,
                                      z:result.data[seriesIndex],
                                      name:seriesIndex
                                  }
                              }
                              if(payloadArray[configIndex].seriesOverrides){
                                  newSeries=Object.assign({}, newSeries, payloadArray[configIndex].seriesOverrides)
                              } else if (me.seriesOverrides){
                                  newSeries=Object.assign({}, newSeries, me.seriesOverrides)
                              }
                              if(me.colorAxis&&newSeries.mode==="markers"){
                                  if(!newSeries.marker){
                                      newSeries.marker={}
                                  }
                                  newSeries.marker.color=me.colorAxisValues
                                  newSeries.marker.colorscale=me.colorAxisScale ? me.colorAxisScale : colorscaleValue
                                  newSeries.marker.colorbar={
                                      thickness:20,
                                      title:me.colorAxisTitle
                                  }
                                  if(newSeries.opacity){
                                      newSeries.marker.opacity=newSeries.opacity
                                  }
                                  if (me.layoutOverrides.colorscaleUseCustomAxis){
                                    newSeries.marker.cmin=me.layoutOverrides.colorscaleAxisMin
                                    newSeries.marker.cmax=me.layoutOverrides.colorscaleAxisMax
                                  }
                              }
                              if(me.markerSizeAxis&&newSeries.mode==="markers"){
                                  if(!newSeries.marker){
                                      newSeries.marker={}
                                  }
                                  newSeries.marker.size=me.markerSizeAxisValues
                                  newSeries.marker.sizemode='area'
                                  let factor= newSeries.maxMarkerSize ? newSeries.maxMarkerSize : 40
                                  newSeries.marker.sizeref=2.0 * Math.max(...me.markerSizeAxisValues)/(factor**2)
                                  if(newSeries.minMarkerSize){
                                      newSeries.marker.sizemin=newSeries.minMarkerSize
                                  }
                                  if(newSeries.hideBorder){
                                      newSeries.marker.line= {
                                          width:0
                                      }
                                  }
                                  if(newSeries.opacity){
                                      newSeries.marker.opacity=newSeries.opacity
                                  }
                              }
                              if(newSeries.type==="scatter"&&newSeries.mode==="markers"&&newSeries.markerSymbol&&newSeries.markerSymbol!==""){
                                  if(!newSeries.marker){
                                      newSeries.marker={}
                                  }
                                  newSeries.marker.symbol=newSeries.markerSymbol
                              }
                              if (me.resolveIdsToName&&!isNaN(parseInt(seriesIndex))){
                                  let foundId=parseInt(seriesIndex)
                                  let foundSensors=me.$store.state.pointsListStore.pointsList.points_list.filter(x=>x.sensor_id===foundId)
                                  if (foundSensors.length>0){
                                      newSeries.name=foundSensors[0].equipment_name+" - "+foundSensors[0].sensor_name
                                  }
                              }
                              if(me.showSensorDesc&&seriesIndex.split(" - ")[1]){
                                  let sensorDesc=me.getSensorDesc(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                  if(sensorDesc) {
                                      if (me.layout.descOnlyName) {
                                          newSeries.name = sensorDesc
                                      } else {
                                          newSeries.name = newSeries.name + " - " + sensorDesc
                                      }
                                  }
                              }
                              if(me.showUnits&&seriesIndex.split(" - ")[1]){
                                  let unit=me.getUnit(seriesIndex.split(" - ")[1],seriesIndex.split(" - ")[0])
                                  if(payloadArray[configIndex].seriesOverrides&&payloadArray[configIndex].seriesOverrides.customUnit){
                                      unit = payloadArray[configIndex].seriesOverrides.customUnit
                                  }
                                  let unitbox=unit&&unit!=="_" ? " ("+unit+")" : ''
                                  newSeries.name=newSeries.name+unitbox
                              }
                              if(newSeries.renderAsSmoothLine&&(!payloadArray[configIndex].seriesOverrides.type||payloadArray[configIndex].seriesOverrides.type==="scatter")&&me.xAxis!=="timestamp"){
                                  let cleanedX=[]
                                  let cleanedY=[]
                                  for(let i in newSeries.x){
                                      if(newSeries.x[i]>0&&newSeries.y[i]>0){
                                          cleanedX.push(newSeries.x[i])
                                          cleanedY.push(newSeries.y[i])
                                      }
                                  }
                                  let smoothOptions = {
                                      span: newSeries.smoothLineSpan ? newSeries.smoothLineSpan : 0.6,
                                      band: newSeries.smoothLineBand ? newSeries.smoothLineBand : 0.8,
                                      degree: newSeries.smoothLineDegree ? newSeries.smoothLineDegree : 1
                                  }
                                  let smooth = me.smoothLineLoess(cleanedX, cleanedY, 50, smoothOptions)
                                  newSeries.x=smooth.data['x']
                                  newSeries.y=smooth.fitted['fitted']
                              }
                              if(me.xAxis!=="timestamp"&&result.data.timestamp){
                                  newSeries.hovertext=result.data.timestamp
                              }
                              if (newSeries.type==="bar"&&newSeries.horizontalBarMode){
                                  newSeries.orientation="h"
                                  let x = [...newSeries.x]
                                  newSeries.x=[...newSeries.y]
                                  newSeries.y=x
                              }
                              if (newSeries.type==="bar"&&newSeries.showValuesOnBars){
                                  newSeries.text=newSeries.orientation==="h" ? newSeries.x.map(x=>x!==null ? parseFloat((x).toFixed(2)) : "") : newSeries.y.map(y=>y!==null ? parseFloat((y).toFixed(2)) : "")
                                  newSeries.textposition= 'auto'
                              }
                              newData.push(newSeries)
                          }
                      }
                      configIndex=configIndex+1
                  }
                  let includedPies=newData.filter(s=>s.type==="pie")
                  if(includedPies.length>1){
                      let aggPie=includedPies[0]
                      includedPies.shift()
                      for(let otherPie of includedPies){
                          aggPie.labels=aggPie.labels.concat(otherPie.labels)
                          aggPie.values=aggPie.values.concat(otherPie.values)
                      }
                      newData=newData.filter(s=>s.type!=="pie")
                      newData.push(aggPie)
                  }
                  let includedPC=newData.filter(s=>s.type==="parcoords")
                  if(includedPC.length>1){
                      let aggPC=includedPC[0]
                      includedPC.shift()
                      for(let otherPC of includedPC){
                          aggPC.dimensions=aggPC.dimensions.concat(otherPC.dimensions)
                      }
                      newData=newData.filter(s=>s.type!=="parcoords")
                      newData.push(aggPC)
                  }
                  let includedHeatMaps=newData.filter(s=>s.type==="heatmap")
                  if(includedHeatMaps.length>1){
                      let heatmap=includedHeatMaps[0]
                      includedHeatMaps.shift()
                      for(let otherPie of includedHeatMaps){
                          heatmap.y=heatmap.y.concat(otherPie.y)
                          heatmap.z=heatmap.z.concat(otherPie.z)
                      }
                      newData=newData.filter(s=>s.type!=="heatmap")
                      newData.push(heatmap)
                  }
                  let includedTables=newData.filter(s=>s.type==="table")
                  if(includedTables.length>1){
                      let aggTable=includedTables[0]
                      includedTables.shift()
                      for(let otherTable of includedTables){
                          aggTable.header.values=aggTable.header.values.concat(otherTable.header.values.slice(1))
                          aggTable.cells.values=aggTable.cells.values.concat(otherTable.cells.values.slice(1))
                      }
                      newData=newData.filter(s=>s.type!=="table")
                      newData.push(aggTable)
                  }
                  for (let summaryType of ["bar summary","scatter summary"]){
                      let includedSummaries=newData.filter(s=>s.summaryType&&s.summaryType===summaryType)
                      if(includedSummaries.length>1&&!includedSummaries[0].disableStacking){
                          let summarySeries=includedSummaries[0]
                          includedSummaries.shift()
                          for(let otherSummary of includedSummaries){
                              summarySeries.x=summarySeries.x.concat(otherSummary.x)
                              summarySeries.y=summarySeries.y.concat(otherSummary.y)
                              if(summarySeries.text&&otherSummary.text){
                                  summarySeries.text=summarySeries.text.concat(otherSummary.text)
                              }
                              if(summarySeries.marker&&summarySeries.marker.color&&otherSummary.marker&&otherSummary.marker.color){
                                  summarySeries.marker.color=summarySeries.marker.color.concat(otherSummary.marker.color)
                              }
                          }
                          newData=newData.filter(s=>!s.summaryType||s.summaryType!==summaryType)
                          newData.push(summarySeries)
                      }
                  }
                  for (let finalItem of newData){
                      if (finalItem.yaxis&&finalItem.yaxis.value){
                          finalItem.yaxis=finalItem.yaxis.value
                      }
                  }
                  me.data = newData
                  setTimeout(function(){me.loading = false},10)
          } else {
              this.loading = false
          }

    }
  },
   mounted () {
      this.facility_name = this.$store.state.pointsListStore.pointsList.facility_name
      this.refresh()
  }
}

</script>

<style scoped>

</style>
